import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { labelText } from "../common/sharedFunctions";
import { Box, Grid, MenuItem, Select, Typography } from '@mui/material';
import localization from '../components/Table/Localization';
import { getModuleActionPermissions, filterEditableActions } from "common/src/actions/userPermissions";
import AdminDataTable from "../components/AdminDataTable";
import AdminStatusBadge from "../components/AdminStatus";
import { PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const REGION_OPTIONS = [
  "Metro Manila",
  "Luzon",
  "Visayas",
  "Mindanao"
];

const sortAlpha = (a, b) => {
  return String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });
};

const optionalNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

const validateLatitude = (value) => {
  if (value === undefined || value === null || value === "") return true;
  const parsed = optionalNumber(value);
  return parsed !== null && parsed >= -90 && parsed <= 90;
};

const validateLongitude = (value) => {
  if (value === undefined || value === null || value === "") return true;
  const parsed = optionalNumber(value);
  return parsed !== null && parsed >= -180 && parsed <= 180;
};

const validateRadius = (value) => {
  if (value === undefined || value === null || value === "") return true;
  const parsed = optionalNumber(value);
  return parsed !== null && parsed > 0;
};

const validateYardGeofenceFields = (yard) => {
  if (!validateLatitude(yard.latitude)) return "Latitude must be between -90 and 90.";
  if (!validateLongitude(yard.longitude)) return "Longitude must be between -180 and 180.";
  if (!validateRadius(yard.geofenceRadiusMeters)) return "Geofence Radius Meters must be a positive number.";
  return "";
};

export default function ContainerYards() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    fetchContainerYards,
    addContainerYard,
    updateContainerYard,
    deleteContainerYard
  } = api;
  const settings = useSelector(state => state.settingsdata.settings);
  const containeryarddata = useSelector(state => state.containeryards);
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const dispatch = useDispatch();    const auth = useSelector((state) => state.auth);

    const containerYardPermissions = getModuleActionPermissions({
        role: auth && auth.profile && auth.profile.usertype,
        moduleKey: "containerYard",
        accessPermissions: settings && settings.accessPermissions
    });
  const label = (key, fallback) => labelText(t, key, fallback);

  useEffect(() => {
    dispatch(fetchContainerYards());
  }, [dispatch, fetchContainerYards]);

  useEffect(() => {
    if (containeryarddata.yards) {
      setData(containeryarddata.yards);
    } else {
      setData([]);
    }
  }, [containeryarddata.yards]);

  const regionLookup = useMemo(() => (
    REGION_OPTIONS.reduce((acc, item) => ({ ...acc, [item]: item }), {})
  ), []);

  const columns = [
    {
      title: label('container_yard_name', 'Container Yard Name'),
      field: 'name',
      defaultSort: 'asc',
      customFilterAndSearch: (term, rowData) => String(rowData.name || "").toLowerCase().includes(String(term || "").toLowerCase()),
      customSort: (a, b) => sortAlpha(a.name, b.name),
      render: rowData => <span>{rowData.name || ""}</span>
    },
    {
      title: label('shipping_line_name', 'Shipping Line Name'),
      field: 'shippingLineName',
      customFilterAndSearch: (term, rowData) => String(rowData.shippingLineName || "").toLowerCase().includes(String(term || "").toLowerCase()),
      customSort: (a, b) => sortAlpha(a.shippingLineName, b.shippingLineName),
      render: rowData => <span>{rowData.shippingLineName || ""}</span>
    },
    {
      title: label('region', 'Region'),
      field: 'region',
      lookup: regionLookup,
      customFilterAndSearch: (term, rowData) => String(rowData.region || "").toLowerCase().includes(String(term || "").toLowerCase()),
      customSort: (a, b) => sortAlpha(a.region, b.region),
      render: rowData => <span>{rowData.region || ""}</span>,
      editComponent: (props) => (
        <Select
          fullWidth
          value={props.value || ""}
          onChange={(event) => props.onChange(event.target.value)}
          displayEmpty
        >
          <MenuItem value="">{label('select', 'Select')}</MenuItem>
          {REGION_OPTIONS.map((item) => (
            <MenuItem key={item} value={item}>{item}</MenuItem>
          ))}
        </Select>
      )
    },
    {
      title: label('container_yard_location', 'Location'),
      field: 'location',
      customFilterAndSearch: (term, rowData) => String(rowData.location || "").toLowerCase().includes(String(term || "").toLowerCase()),
      customSort: (a, b) => sortAlpha(a.location, b.location)
    },
    { title: label('available_slots', 'Available Slots'), field: 'availableSlots', type: 'numeric', initialEditValue: 0 },
    { title: label('total_slots', 'Total Slots'), field: 'totalSlots', type: 'numeric', initialEditValue: 0 },
    {
      title: label('latitude', 'Latitude'),
      field: 'latitude',
      type: 'numeric',
      validate: rowData => validateLatitude(rowData.latitude) ? true : label('latitude_validation_error', 'Latitude must be between -90 and 90.')
    },
    {
      title: label('longitude', 'Longitude'),
      field: 'longitude',
      type: 'numeric',
      validate: rowData => validateLongitude(rowData.longitude) ? true : label('longitude_validation_error', 'Longitude must be between -180 and 180.')
    },
    {
      title: label('geofence_radius_meters', 'Geofence Radius Meters'),
      field: 'geofenceRadiusMeters',
      type: 'numeric',
      render: rowData => rowData.geofenceRadiusMeters || label('default_300_meters', 'Default 300m'),
      validate: rowData => validateRadius(rowData.geofenceRadiusMeters) ? true : label('geofence_radius_validation_error', 'Geofence Radius Meters must be a positive number.')
    },
    {
      title: t('active_status'),
      field: 'active',
      type: 'boolean',
      initialEditValue: true,
      render: rowData => <AdminStatusBadge status={rowData.active === false ? "INACTIVE" : "ACTIVE"}>{rowData.active === false ? t('false') : t('true')}</AdminStatusBadge>
    }
  ];

  return (
    containeryarddata.loading ? <CircularLoading /> :
      <Box className="garahex-container-yards-page">
        <PageHeader
          eyebrow={label("operations", "Operations")}
          title={label('container_yards_title', 'Container Yards')}
          description={label("container_yards_description", "Maintain yard names, shipping lines, locations, capacity, geofence values, and active status through the existing yard workflow.")}
          actions={<StatusBadge tone="info">{`${data.length} ${label("records", "records")}`}</StatusBadge>}
        />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <SurfaceCard sx={{ minHeight: 110 }}>
              <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label('container_yards_title', 'Container Yards')}</Typography>
              <Typography sx={{ color: adminTokens.colors.text, fontSize: "1.8rem", fontWeight: 950 }}>{data.length}</Typography>
            </SurfaceCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <SurfaceCard sx={{ minHeight: 110 }}>
              <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label('active_status', 'Active')}</Typography>
              <Typography sx={{ color: adminTokens.colors.success, fontSize: "1.8rem", fontWeight: 950 }}>{data.filter((yard) => yard.active !== false).length}</Typography>
            </SurfaceCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <SurfaceCard sx={{ minHeight: 110 }}>
              <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{label('available_slots', 'Available Slots')}</Typography>
              <Typography sx={{ color: adminTokens.colors.royalBlue, fontSize: "1.8rem", fontWeight: 950 }}>{data.reduce((sum, yard) => sum + (optionalNumber(yard.availableSlots) || 0), 0)}</Typography>
            </SurfaceCard>
          </Grid>
        </Grid>
        <SurfaceCard>
          <SectionHeader
            title={label('container_yards_title', 'Container Yards')}
            description={label("container_yards_table_description", "Existing inline add, edit, delete, validation, export, search, and sort behavior remains active.")}
          />
        <AdminDataTable
          title={label('container_yards_title')}
          columns={columns}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
          }}
          data={data}
          emptyTitle={label("no_container_yards_found", "No container yards found")}
          emptyDescription={label("no_container_yards_found_description", "No yard records are available in the current operations scope.")}
          onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
          options={{
            exportButton: true,
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData.id ? "rgba(11, 124, 255, 0.09)" : "#FFFFFF"
            }),
          }}
          localization={localization(t)}
          editable={settings.AllowCriticalEditsAdmin ? filterEditableActions({ editable: {
            onRowAdd: newData =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  if (!(newData && newData.name)) {
                    alert(t('no_details_error'));
                    reject();
                  } else {
                    const geofenceError = validateYardGeofenceFields(newData);
                    if (geofenceError) {
                      alert(geofenceError);
                      reject();
                      return;
                    }
                    const payload = {
                      ...newData,
                      shippingLineName: newData.shippingLineName || "",
                      region: newData.region || ""
                    };
                    dispatch(addContainerYard(payload)).then(resolve).catch((error) => {
                      alert(error && error.message ? error.message : t('no_details_error'));
                      reject();
                    });
                  }
                }, 600);
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  if (!(newData && newData.name)) {
                    alert(t('no_details_error'));
                    reject();
                  } else {
                    if (newData !== oldData) {
                      const geofenceError = validateYardGeofenceFields(newData);
                      if (geofenceError) {
                        alert(geofenceError);
                        reject();
                        return;
                      }
                      const payload = {
                        ...newData,
                        shippingLineName: newData.shippingLineName || "",
                        region: newData.region || ""
                      };
                      dispatch(updateContainerYard(payload)).then(resolve).catch((error) => {
                        alert(error && error.message ? error.message : t('no_details_error'));
                        reject();
                      });
                    } else {
                      resolve();
                    }
                  }
                }, 600);
              }),
            onRowDelete: oldData =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  dispatch(deleteContainerYard(oldData)).then(resolve).catch((error) => {
                    alert(error && error.message ? error.message : t('no_details_error'));
                    reject();
                  });
                }, 600);
              }),
          }, permissions: containerYardPermissions }) : null}
        />
        </SurfaceCard>
      </Box>
  );
}
